// Mirror of v2/server/service_area/geo.go in kalos-core. The duplication is
// deliberate: Schedule Call has to warn while the operator is still deciding,
// before anything is saved, so the browser cannot ask the backend for the answer
// it is about to stamp on the event. Both sides run the same formula over the
// same service_area row, and the two must stay behaviourally identical -- a
// change here without the matching change there gets an address warned about in
// Schedule Call and stamped in-area on save.
//
// No google.maps: Schedule Call does not load the Maps script.

export type ServiceAreaPoint = { lat: number; lng: number };

// Matches earthRadiusMiles in geo.go, which in turn matches servicechannel's
// milesBetween rather than toolkit.HaversineDistance's 6371km.
const EARTH_RADIUS_MILES = 3958.7613;

// The length of one degree of latitude. One degree of longitude is this scaled
// by cos(latitude).
const MILES_PER_DEGREE = (Math.PI / 180) * EARTH_RADIUS_MILES;

// parseServiceAreaPolygon decodes a ServiceArea.polygon value into its vertices.
// The stored ring is open -- the last vertex joins the first and is not repeated
// -- and both helpers below assume that, closing the ring themselves.
//
// Anything that will not parse, or that is not an array of numeric {lat,lng},
// yields an empty polygon rather than throwing. That is the same inert outcome
// geo.go reaches by a different route: there ParsePolygon returns an error and
// the caller leaves the flag at 0, here the empty polygon makes every point read
// as inside. A boundary that cannot be read is not worth a broken map or a
// blocked booking.
export const parseServiceAreaPolygon = (raw: string): ServiceAreaPoint[] => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }

  if (!Array.isArray(parsed)) return [];

  const points: ServiceAreaPoint[] = [];
  for (const entry of parsed) {
    if (
      typeof entry !== 'object' ||
      entry === null ||
      typeof (entry as ServiceAreaPoint).lat !== 'number' ||
      typeof (entry as ServiceAreaPoint).lng !== 'number'
    ) {
      return [];
    }
    points.push({ lat: (entry as ServiceAreaPoint).lat, lng: (entry as ServiceAreaPoint).lng });
  }
  return points;
};

// isInsideServiceArea mirrors PointInPolygon in geo.go: ray casting, counting the
// polygon edges a ray east of the point crosses, where an odd count means inside.
//
// The comparison is half-open, so a point exactly on the boundary reads as inside
// on the south and west edges and outside on the north and east. That is
// deterministic but edge-dependent, and deliberately not corrected for -- a
// coordinate landing exactly on the boundary to the seventh decimal is not a real
// case, and the flag it feeds is advisory either way.
//
// A polygon of fewer than three vertices reads as not-inside, exactly as geo.go
// has it. That is not the inert answer -- callers that treat "not inside" as
// "outside" have to check for an absent boundary themselves, the way the Go event
// service checks PolygonCache.Active for nil before it stamps anything.
// milesOutsideServiceArea needs no such check: it returns 0 for a degenerate
// polygon, so a missing boundary reads as nothing to warn about.
export const isInsideServiceArea = (
  point: ServiceAreaPoint,
  polygon: ServiceAreaPoint[],
): boolean => {
  if (polygon.length < 3) return false;

  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const yi = polygon[i].lat;
    const xi = polygon[i].lng;
    const yj = polygon[j].lat;
    const xj = polygon[j].lng;
    // Named rather than inlined only because the inlined form mixes > and !== in
    // one expression. The && still short-circuits, so the edge's slope is never
    // divided by zero for an edge that does not straddle the ray.
    const iAbove = yi > point.lat;
    const jAbove = yj > point.lat;
    if (iAbove !== jAbove && point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
};

// milesOutsideServiceArea mirrors MilesToNearestEdge in geo.go: 0 when the point
// is inside, otherwise the distance in miles to the closest point on the closest
// edge.
//
// The polygon is projected onto a flat east/north plane in miles centred on the
// point, and the distance measured there. Over the tens of miles this reports,
// that costs far less accuracy than the geocode feeding it.
export const milesOutsideServiceArea = (
  point: ServiceAreaPoint,
  polygon: ServiceAreaPoint[],
): number => {
  if (polygon.length < 3 || isInsideServiceArea(point, polygon)) return 0;

  // Degrees of longitude shrink towards the poles; at this latitude the factor
  // is about 0.88.
  const lngScale = Math.cos((point.lat * Math.PI) / 180) * MILES_PER_DEGREE;

  let best = Infinity;
  for (let i = 0; i < polygon.length; i += 1) {
    const next = polygon[(i + 1) % polygon.length];

    const ax = (polygon[i].lng - point.lng) * lngScale;
    const ay = (polygon[i].lat - point.lat) * MILES_PER_DEGREE;
    const bx = (next.lng - point.lng) * lngScale;
    const by = (next.lat - point.lat) * MILES_PER_DEGREE;

    const d = distanceToSegment(ax, ay, bx, by);
    if (d < best) best = d;
  }
  return best;
};

// distanceToSegment is the distance from the origin to the segment a->b, with
// every coordinate already in miles relative to the point being measured.
const distanceToSegment = (ax: number, ay: number, bx: number, by: number): number => {
  const dx = bx - ax;
  const dy = by - ay;

  // A repeated vertex gives a zero-length edge; fall back to the point.
  if (dx === 0 && dy === 0) return Math.hypot(ax, ay);

  // Project the origin onto the infinite line, then clamp to the segment.
  const t = Math.max(0, Math.min(1, -(ax * dx + ay * dy) / (dx * dx + dy * dy)));

  return Math.hypot(ax + t * dx, ay + t * dy);
};
